import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { MarketSignalExplainerContent } from '../../constants/marketSignalExplainers';

export interface MarketSignalExplainerModalProps {
  open: boolean;
  onClose: () => void;
  content: MarketSignalExplainerContent;
  /** Human-readable current status, e.g. "Low Participation" */
  currentLabel?: string;
  currentDetail?: string;
  masked?: boolean;
}

const MarketSignalExplainerModal: React.FC<MarketSignalExplainerModalProps> = ({
  open,
  onClose,
  content,
  currentLabel,
  currentDetail,
  masked = false,
}) => {
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  useEffect(() => {
    if (!open) {
      setShowAdvanced(false);
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const highlightLabel = !masked ? currentLabel : undefined;

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="signal-explainer-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          maxWidth: 520,
          width: '100%',
          maxHeight: 'min(90vh, 720px)',
          overflow: 'auto',
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
        }}
      >
        <div
          style={{
            padding: '18px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 1,
          }}
        >
          <div>
            <h2
              id="signal-explainer-title"
              style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}
            >
              {content.title}
            </h2>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
              {content.intro}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              color: '#475569',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '16px 20px 20px' }}>
          {masked ? (
            <div
              style={{
                marginBottom: 16,
                padding: '12px 14px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 13,
                color: '#475569',
                lineHeight: 1.55,
              }}
            >
              {content.maskedMessage}
            </div>
          ) : currentLabel ? (
            <div
              style={{
                marginBottom: 16,
                padding: '12px 14px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#1d4ed8',
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  marginBottom: 4,
                }}
              >
                Right now
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1e3a8a' }}>{currentLabel}</div>
              {currentDetail && (
                <div style={{ fontSize: 13, color: '#334155', marginTop: 4, lineHeight: 1.5 }}>
                  {currentDetail}
                </div>
              )}
            </div>
          ) : null}

          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#64748b',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            All possible statuses
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {content.statuses.map((status) => {
              const isCurrent =
                !!highlightLabel &&
                status.label.toUpperCase() === highlightLabel.toUpperCase();
              return (
                <div
                  key={status.label}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: isCurrent ? '2px solid #2563eb' : '1px solid #e2e8f0',
                    background: isCurrent ? '#f0f9ff' : '#fafafa',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{status.label}</span>
                    {isCurrent && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#1d4ed8',
                          background: '#dbeafe',
                          padding: '2px 8px',
                          borderRadius: 20,
                        }}
                      >
                        CURRENT
                      </span>
                    )}
                  </div>
                  {status.trigger && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                      <strong>When:</strong> {status.trigger}
                    </div>
                  )}
                  <div style={{ fontSize: 13, color: '#334155', marginTop: 6, lineHeight: 1.5 }}>
                    {status.meaning}
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 16,
              padding: '12px 14px',
              background: '#f8fafc',
              borderRadius: 10,
              fontSize: 13,
              color: '#475569',
              lineHeight: 1.55,
            }}
          >
            <strong style={{ color: '#334155' }}>How this relates to other cards: </strong>
            {content.relatedCards}
          </div>

          {content.advancedLines.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#2563eb',
                  cursor: 'pointer',
                }}
              >
                {showAdvanced ? '▼ Hide technical details' : '▶ How is this calculated?'}
              </button>
              {showAdvanced && (
                <ul
                  style={{
                    margin: '8px 0 0',
                    paddingLeft: 18,
                    fontSize: 12,
                    color: '#64748b',
                    lineHeight: 1.6,
                  }}
                >
                  {content.advancedLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MarketSignalExplainerModal;

/** Small info control — separate from card body so masked blur does not block clicks */
export const SignalCardInfoButton: React.FC<{
  onClick: () => void;
  color?: string;
  ariaLabel?: string;
}> = ({ onClick, color = '#64748b', ariaLabel = 'Explain this signal' }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    aria-label={ariaLabel}
    title="What does this mean?"
    style={{
      flexShrink: 0,
      width: 28,
      height: 28,
      borderRadius: '50%',
      border: `1px solid ${color}44`,
      background: '#ffffffcc',
      color,
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 'auto',
    }}
  >
    ?
  </button>
);
